<template lang="pug">
div.mobile-filter-wrapper.mt-6
  h5.mb-3.mt-6
    | Filter
  div.filter-tabs.d-flex.gap-2.overflow-auto.align-items-start.flex-wrap.pb-2
    button.btn.btn-outline-secondary.btn-sm(
      @click="toggleFilter('all')"
      :class="{ 'active': showAll }"
    )
      | Alle Filter
    button.btn.btn-outline-secondary.btn-sm.text-nowrap.position-relative(
      v-for="tab in filterTabs"
      :key="tab.key"
      @click="toggleFilter(tab.key)"
      :class="{ 'active': activeFilter === tab.key }"
    )
      | {{ tab.title }}
      span.badge.bg-primary.rounded-pill.ms-1(v-if="getActiveCount(tab.key) > 0")
        | {{ getActiveCount(tab.key) }}

  div.filter-content(v-if="facets && (activeFilter || showAll)")
    div.card.mb-3
      div.card-header.d-flex.justify-content-between.align-items-center
        h6.mb-0 {{ filterHeaderTitle }}
        button.btn.btn-sm.btn-outline-secondary(@click="closeFilters")
          i.fas.fa-times

      div.card-body
        template(v-for="filterConfig in displayedFilters" :key="filterConfig.key")
          div(
            v-if="filterConfig.key === 'salary_available'"
            :class="filterConfig.containerClass"
          )
            p.mb-0.fw-bold {{ filterConfig.title }}
            Checkbox(
              v-model="chosenFilters.salary_available"
              :value="facets.salary_available[0]"
            ) {{ $t("js.components.filter_wizard.only_with_salary") }}

          component(
            v-else
            :is="filterConfig.component"
            v-bind="filterConfig.props"
            v-model="chosenFilters[filterConfig.key]"
            :class="filterConfig.containerClass"
          )

        template(v-if="showAll")
          h5.mt-4 {{ $t('js.jobs.search.sort_order_label') }}
          .d-flex.gap-2.align-items-center
            select.form-select.w-fit-content(v-model="order" @change="onSortChange")
              option(v-for="key in sortKeys" :key="key" :value="key")
                | {{ $t(`js.jobs.search.search_order.${key}.button`) }}

      button.btn.btn-primary.btn-block.w-100(
        @click="submitQuery"
        :disabled="isLoading"
        :class="{ 'disabled': isLoading }"
      )
        span(v-if="isLoading")
          i.fas.fa-spinner.fa-spin.me-2
          | {{ $t("js.components.filter_wizard.loading") }}
        span(v-else)
          | {{ $t("js.components.filter_wizard.show_results") }}
</template>

<script setup lang="ts">
import { onMounted, computed, ref } from "vue"
import ExCheckboxGroup from "./wizard/extendable-checkbox-group.vue"
import Checkbox from "@/elements/Checkbox.vue"
import RemoteRadio from "./wizard/remote-radio.vue"
import { useI18n } from "vue-i18n"
import { query, searchUrl, chosenFilters, currentFacets as facets, setFacetsFromUrl, initializeFacets } from "./store"
import type { FacetType } from "./store"

const props = defineProps<{
  searchProps: {
    q: string[];
    currentUrl: string;
    subJobType?: string;
  };
  frontendFacets: FacetType;
  sortKeys: string[];
}>();

const $t = useI18n().t
const idPrefix = computed(() => "fw_mobile_" + Math.floor(Math.random() * 10000))
const activeFilter = ref<string | null>(null)
const showAll = ref(false)
const isLoading = ref(false)
const order = ref('relevance')

const filterConfigs = computed(() => ({
  sub_job_type: {
    component: ExCheckboxGroup,
    title: $t('js.components.search.filter_wizard.stellenart'),
    props: {
      prefix: idPrefix.value,
      options: facets.value?.sub_job_type,
      translate: true,
      title: $t('js.components.search.filter_wizard.stellenart')
    },
    containerClass: 'mb-3'
  },
  location_names: {
    component: ExCheckboxGroup,
    title: $t('js.components.search.filter_wizard.arbeitsort'),
    props: {
      prefix: idPrefix.value,
      options: facets.value?.location_names,
      title: $t('js.components.search.filter_wizard.arbeitsort')
    },
    containerClass: 'mb-3'
  },
  job_type: {
    component: ExCheckboxGroup,
    title: $t('js.components.search.filter_wizard.anstellungsart'),
    props: {
      prefix: idPrefix.value,
      options: facets.value?.job_type,
      title: $t('js.components.search.filter_wizard.anstellungsart')
    },
    containerClass: 'mb-3'
  },
  remote_type: {
    component: RemoteRadio,
    title: $t('js.components.search.filter_wizard.homeoffice_arbeitsplatz'),
    props: {
      options: facets.value?.remote_type,
      title: $t('js.components.search.filter_wizard.homeoffice_arbeitsplatz'),
      translate: true
    },
    containerClass: 'mb-3'
  },
  working_time: {
    component: ExCheckboxGroup,
    title: $t('js.components.search.filter_wizard.arbeitszeit'),
    props: {
      prefix: idPrefix.value,
      options: facets.value?.working_time,
      title: $t('js.components.search.filter_wizard.arbeitszeit')
    },
    containerClass: 'mb-3'
  },
  portal_type_tag: {
    component: ExCheckboxGroup,
    title: $t('js.components.search.filter_wizard.branche'),
    props: {
      prefix: idPrefix.value,
      options: facets.value?.portal_type_tag,
      title: $t('js.components.search.filter_wizard.branche'),
      translate: true
    },
    containerClass: 'mb-3'
  },
  salary_available: {
    component: 'div',
    title: $t('js.components.search.filter_wizard.gehalt'),
    props: {},
    containerClass: 'mb-3',
    isCustom: true
  }
}))

const filterTabs = computed(() =>
  Object.entries(filterConfigs.value)
    .filter(([key]) => facets.value?.hasOwnProperty(key))
    .map(([key, config]) => ({
      key,
      title: config.title,
      show: true
    }))
)

const displayedFilters = computed(() => {
  if (showAll.value) {
    return Object.entries(filterConfigs.value)
      .filter(([key]) => facets.value?.hasOwnProperty(key))
      .map(([key, config]) => ({ key, ...config }))
  } else if (activeFilter.value) {
    const config = filterConfigs.value[activeFilter.value as keyof typeof filterConfigs.value]
    return config ? [{ key: activeFilter.value, ...config }] : []
  }
  return []
})

const filterHeaderTitle = computed(() => {
  if (showAll.value) {
    return 'Alle Filter'
  }
  return filterTabs.value.find(t => t.key === activeFilter.value)?.title || ''
})

onMounted(async () => {
  query.value = props.searchProps.q.join(" ")
  const currentUrl = new URL(props.searchProps.current_url, window.location.toString())
  initializeFacets(props.frontendFacets)
  setFacetsFromUrl(currentUrl, {})
  order.value = new URLSearchParams(window.location.search).get('order') || order.value
})

const submitQuery = (ev: Event) => {
  isLoading.value = true
  ev.preventDefault()
  Turbo.visit(searchUrl.value)
}

const toggleFilter = (filterKey: string) => {
  if (filterKey === 'all') {
    showAll.value = !showAll.value
    activeFilter.value = null
  } else {
    showAll.value = false
    activeFilter.value = activeFilter.value === filterKey ? null : filterKey
  }
}

const closeFilters = () => {
  showAll.value = false
  activeFilter.value = null
}

const getActiveCount = (filterKey: string) => {
  const selected = chosenFilters[filterKey as keyof typeof chosenFilters]
  return Array.isArray(selected) ? selected.length : (selected ? 1 : 0)
}

function onSortChange() {
  const url = new URL(window.location.href)
  url.searchParams.set('order', order.value)
  Turbo.visit(url.toString())
}
</script>

<style scoped>
.mobile-filter-wrapper {
  padding: 0 15px;
}

.filter-tabs {
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.filter-tabs::-webkit-scrollbar {
  display: none;
}

.btn-outline-primary.active {
  background-color: var(--bs-primary);
  color: white;
}

.filter-content {
  animation: slideIn 0.2s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.badge {
  font-size: 0.65em;
  min-width: 1.1em;
}
</style>
